import type { MeasurementProfile, MeasurementProfileId } from "./types.js";
import { childChartProfile } from "./childSizeCharts.js";

/**
 * Predefined measurement profiles
 */
export const measurementProfiles: Record<
  MeasurementProfileId,
  MeasurementProfile
> = {
  small: {
    label: "Small",
    description: "88-96 cm chest",
    figure: "male",
    values: {
      chest: 92,
      seat: 94,
      naturalWaist: 78,
      trouserWaist: 82,
      halfBack: 19,
      backNeckToWaist: 43.4,
      scyeDepth: 22.8,
      neck: 38,
      onePieceSleeveLength: 64,
      twoPieceSleeveLength: 80,
      insideLeg: 78,
      bodyRise: 27.6,
      wrist: 17,
      trouserBottomWidth: 21.8,
    },
  },
  medium: {
    label: "Medium",
    description: "96-104 cm chest",
    figure: "male",
    values: {
      chest: 100,
      seat: 102,
      naturalWaist: 86,
      trouserWaist: 90,
      halfBack: 20,
      backNeckToWaist: 44.2,
      scyeDepth: 24.4,
      neck: 40,
      onePieceSleeveLength: 65,
      twoPieceSleeveLength: 82,
      insideLeg: 80,
      bodyRise: 28.2,
      wrist: 17.8,
      trouserBottomWidth: 22.6,
    },
  },
  large: {
    label: "Large",
    description: "104-112 cm chest",
    figure: "male",
    values: {
      chest: 108,
      seat: 110,
      naturalWaist: 94,
      trouserWaist: 98,
      halfBack: 21,
      backNeckToWaist: 45,
      scyeDepth: 26,
      neck: 42,
      onePieceSleeveLength: 66,
      twoPieceSleeveLength: 84,
      insideLeg: 82,
      bodyRise: 28.8,
      wrist: 18.6,
      trouserBottomWidth: 23.4,
    },
  },
  "x-large": {
    label: "X-Large",
    description: "112-120 cm chest",
    figure: "male",
    values: {
      chest: 116,
      seat: 118,
      naturalWaist: 102,
      trouserWaist: 106,
      halfBack: 22,
      backNeckToWaist: 45.8,
      scyeDepth: 27.6,
      neck: 44,
      onePieceSleeveLength: 67,
      twoPieceSleeveLength: 86,
      insideLeg: 84,
      bodyRise: 29.4,
      wrist: 19.4,
      trouserBottomWidth: 24.2,
    },
  },
  chris: {
    // Initials only: the app is public.
    label: "CD",
    description: "Example: measured shirt and T-shirt sizes, tall",
    figure: "male",
    // 191 cm, about 7.8 heads tall.
    stature: { height: 1910, headHeight: 245, headWidth: 162, headDepth: 205 },
    values: {
      chest: 116,
      shirtLength: 66,
      naturalWaist: 92.5,
      trouserWaist: 96,
      seat: 114.5,
      halfBack: 26,
      backNeckToWaist: 50,
      scyeDepth: 22.5,
      neck: 48,
      onePieceSleeveLength: 67,
      shortOnePieceSleeveLength: 24,
      twoPieceSleeveLength: 88,
      insideLeg: 85,
      bodyRise: 25.5,
      wrist: 20,
      armCircumference: 43,
      trouserBottomWidth: 26.2,
    },
  },
  anca: {
    label: "AM",
    description: "Example: men's small upper body with a separately measured lower body",
    figure: "female",
    // 172 cm, about 7.75 heads tall.
    stature: { height: 1720, headHeight: 222, headWidth: 146, headDepth: 185 },
    values: {
      chest: 92,
      seat: 97,
      naturalWaist: 71,
      trouserWaist: 73,
      halfBack: 19,
      backNeckToWaist: 43.4,
      scyeDepth: 22.8,
      neck: 38,
      onePieceSleeveLength: 64,
      twoPieceSleeveLength: 80,
      insideLeg: 72,
      bodyRise: 26,
      wrist: 17,
      trouserBottomWidth: 21.8,
    },
  },
  haydn: {
    label: "HM",
    description: "Example: from a measurement sheet",
    figure: "male",
    // 175 cm, about 7.8 heads tall.
    stature: { height: 1750, headHeight: 224, headWidth: 148, headDepth: 187 },
    values: {
      backNeckToWaist: 43,
      jacketLength: 67,
      halfBack: 21.5,
      chest: 107,
      naturalWaist: 93,
      trouserWaist: 97,
      seat: 107,
      neck: 42,
      onePieceSleeveLength: 58,
      twoPieceSleeveLength: 81,
      elbowLength: 48,
      trouserSideSeam: 96,
      // Measured low.
      bodyRise: 23,
      insideLeg: 72,
      // Approximate ("20ish").
      trouserBottomWidth: 20,
      wrist: 16,
      armCircumference: 33,
      // Sheet says 42; 24 is the likely intended value.
      scyeDepth: 24,
    },
  },
  // Aldrich, Metric Pattern Cutting (4th ed.), p.11, size 12: the form
  // cutting default. Its "chest" is a front width (frontChestWidth), not a
  // girth, so `chest` stays unset. Back width is halved for halfBack.
  "aldrich-12": {
    label: "Aldrich size 12",
    description: "Aldrich size chart, 88 cm bust",
    figure: "female",
    values: {
      bust: 88,
      naturalWaist: 70,
      seat: 94,
      halfBack: 17.2,
      frontChestWidth: 32.4,
      shoulder: 12.25,
      neck: 37,
      bustDart: 7,
      armCircumference: 28.4,
      wrist: 16,
      ankle: 24,
      highAnkle: 21,
      backNeckToWaist: 41,
      frontShoulderToWaist: 41,
      scyeDepth: 21,
      waistToKnee: 58.5,
      waistToHip: 20.6,
      waistToFloor: 104,
      bodyRise: 28,
      onePieceSleeveLength: 58.5,
      jerseySleeveLength: 52.5,
      trouserBottomWidth: 22,
    },
  },
  // Aldrich p.12, High Street fashion Medium: the flat cutting default.
  "high-street-medium": {
    label: "High Street Medium",
    description: "Aldrich High Street chart, 88 cm bust",
    figure: "female",
    values: {
      bust: 88,
      naturalWaist: 70,
      seat: 94,
      halfBack: 17.2,
      frontChestWidth: 32.4,
      shoulder: 12.2,
      neck: 37,
      bustDart: 7,
      armCircumference: 28.4,
      wrist: 16,
      ankle: 24,
      highAnkle: 21,
      backNeckToWaist: 41,
      frontShoulderToWaist: 41,
      scyeDepth: 21,
      waistToKnee: 58.5,
      waistToHip: 20.6,
      waistToFloor: 104,
      bodyRise: 28,
      onePieceSleeveLength: 58,
      jerseySleeveLength: 52,
    },
  },
  // Aldrich children's wear: the sample sizes the book's blocks are worked
  // for, generated from its size charts (pp. 36–38, 348).
  "baby-64": childChartProfile("babies", 64),
  "girl-104": childChartProfile("girls", 104),
  "girl-110": childChartProfile("girls", 110),
  "girl-122": childChartProfile("girls", 122),
  "girl-134": childChartProfile("girls", 134),
  "boy-122": childChartProfile("boys", 122),
  "boy-134": childChartProfile("boys", 134),
  "girl-158-developing": childChartProfile("girlsDeveloping", 158),
};

/**
 * Ordered list of measurement profile IDs
 */
export const measurementProfileOrder: MeasurementProfileId[] = [
  "small",
  "medium",
  "large",
  "x-large",
  "chris",
  "anca",
  "haydn",
  "aldrich-12",
  "high-street-medium",
  "baby-64",
  "girl-104",
  "girl-110",
  "girl-122",
  "girl-134",
  "boy-122",
  "boy-134",
  "girl-158-developing",
];
